import { useState } from 'react';
import SEO from '../../components/SEO';

export default function Partnerships() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    partnershipType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": "Partnership Opportunities - MY AVA Farms Ghana",
        "description": "Partner with MY AVA Farms for BSF farming distribution, research collaboration, investment opportunities, technology partnerships, training affiliates, and export partnerships in Ghana",
        "url": `${siteUrl}/partnerships`,
        "mainEntity": {
          "@type": "Organization",
          "name": "MY AVA Farms",
          "url": siteUrl,
          "description": "Leading Black Soldier Fly farming operation seeking partnerships in distribution, research, investment, technology, training, and export in Ghana",
          "telephone": "+233233371378",
          "email": "myavagh@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "33 Kwame Nkrumah Ave, Adabraka",
            "addressLocality": "Accra",
            "addressCountry": "GH"
          }
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "Partnerships", "item": `${siteUrl}/partnerships` }
        ]
      }
    ]
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Get selected partnership type text from dropdown
      const partnershipSelect = document.getElementById('partnershipType') as HTMLSelectElement;
      const selectedPartnership = partnershipSelect.options[partnershipSelect.selectedIndex].text;

      // Prepare form data with selected text
      const submitData = {
        ...formData,
        partnershipType: selectedPartnership
      };

      const response = await fetch('https://readdy.ai/api/form/d4md2fv7s74mkk8pdv5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(submitData).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          partnershipType: '',
          message: ''
        });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const partnershipTypes = [
    {
      title: 'Distribution Partners',
      icon: 'ri-truck-line',
      description: 'Join our network of distributors to bring sustainable BSF products to new markets across Ghana and West Africa.',
      benefits: [
        'Exclusive territory rights',
        'Marketing support and materials',
        'Competitive wholesale pricing',
        'Product training and certification',
        'Ongoing technical support'
      ],
      image: 'https://public.readdy.ai/ai/img_res/3a20673b44bde70e2d4a347272ff070d.jpg'
    },
    {
      title: 'Research Collaborations',
      icon: 'ri-microscope-line',
      description: 'Partner with us on innovative research projects to advance BSF farming technology and sustainable agriculture practices.',
      benefits: [
        'Access to research facilities',
        'Joint publication opportunities',
        'Shared intellectual property',
        'Grant application support',
        'Academic collaboration network'
      ],
      image: 'https://readdy.ai/api/search-image?query=Black%20soldier%20fly%20larvae%20under%20microscope%20for%20scientific%20research%2C%20BSF%20insect%20biology%20study%2C%20sustainable%20agriculture%20research%20laboratory%2C%20detailed%20macro%20photography%20of%20BSF%20larvae%2C%20professional%20entomology%20research&width=400&height=300&seq=research-collaboration-bsf&orientation=landscape'
    },
    {
      title: 'Investment Opportunities',
      icon: 'ri-funds-line',
      description: 'Invest in the future of sustainable agriculture and be part of Ghana\'s circular economy revolution.',
      benefits: [
        'High-growth market potential',
        'Sustainable impact investment',
        'Transparent financial reporting',
        'Strategic business guidance',
        'Scalable business model'
      ],
      image: 'https://readdy.ai/api/search-image?query=Black%20soldier%20fly%20larvae%20farming%20operation%20showing%20scale%20and%20production%20capacity%2C%20BSF%20insect%20farming%20facility%20with%20multiple%20breeding%20containers%2C%20sustainable%20protein%20production%20investment%20opportunity%2C%20commercial%20BSF%20larvae%20cultivation&width=400&height=300&seq=investment-opportunities-bsf&orientation=landscape'
    },
    {
      title: 'Technology Partners',
      icon: 'ri-settings-3-line',
      description: 'Collaborate on developing and implementing innovative technologies for BSF farming automation and optimization.',
      benefits: [
        'Technology integration support',
        'Pilot project opportunities',
        'Co-development agreements',
        'Market validation access',
        'Innovation showcase platform'
      ],
      image: 'https://readdy.ai/api/search-image?query=Black%20soldier%20fly%20larvae%20in%20automated%20feeding%20system%2C%20BSF%20insect%20farming%20technology%20and%20equipment%2C%20modern%20sustainable%20agriculture%20automation%2C%20BSF%20larvae%20processing%20technology%2C%20innovative%20insect%20farming%20machinery&width=400&height=300&seq=technology-partners-bsf&orientation=landscape'
    },
    {
      title: 'Training Affiliates',
      icon: 'ri-graduation-cap-line',
      description: 'Become a certified training affiliate and help spread BSF farming knowledge across your region.',
      benefits: [
        'Certified trainer program',
        'Training materials provided',
        'Revenue sharing model',
        'Brand recognition',
        'Continuous curriculum updates'
      ],
      image: 'https://readdy.ai/api/search-image?query=Black%20soldier%20fly%20larvae%20demonstration%20for%20training%20purposes%2C%20BSF%20insect%20farming%20education%20and%20hands-on%20learning%2C%20sustainable%20agriculture%20training%20with%20live%20BSF%20larvae%2C%20professional%20insect%20farming%20instruction&width=400&height=300&seq=training-affiliates-bsf&orientation=landscape'
    },
    {
      title: 'Export Partners',
      icon: 'ri-global-line',
      description: 'Partner with us to expand BSF product exports to international markets with full logistics support.',
      benefits: [
        'Export documentation assistance',
        'Quality certification support',
        'Logistics coordination',
        'Market entry guidance',
        'International trade connections'
      ],
      image: 'https://readdy.ai/api/search-image?query=Dried%20black%20soldier%20fly%20larvae%20packaged%20for%20export%2C%20BSF%20insect%20protein%20products%20ready%20for%20international%20markets%2C%20high-quality%20processed%20BSF%20larvae%20for%20global%20distribution%2C%20sustainable%20protein%20export%20products&width=400&height=300&seq=export-partners-bsf&orientation=landscape'
    }
  ];

  const successStories = [
    {
      partner: 'AgroTech Ghana',
      type: 'Distribution Partner',
      result: '300% increase in BSF product distribution across 5 regions',
      quote: 'Partnering with MY AVA Farms has transformed our product portfolio and opened new revenue streams.',
      image: 'https://readdy.ai/api/search-image?query=Successful%20African%20agricultural%20business%20partners%20celebrating%20growth%2C%20professional%20team%20in%20Ghana%20agricultural%20facility%2C%20sustainable%20farming%20partnership%20success%20story%2C%20happy%20business%20collaboration&width=300&height=200&seq=success-story-1&orientation=landscape'
    },
    {
      partner: 'Centre for Animal Research Institute',
      type: 'Research Partner',
      result: '5 joint research papers published on BSF farming optimization',
      quote: 'This collaboration has advanced our understanding of sustainable protein production significantly.',
      image: 'https://readdy.ai/api/search-image?query=African%20university%20researchers%20and%20agricultural%20partners%20in%20laboratory%2C%20successful%20research%20collaboration%20in%20Ghana%2C%20academic%20and%20industry%20partnership%2C%20professional%20scientists%20studying%20BSF%20farming&width=300&height=200&seq=success-story-2&orientation=landscape'
    },
    {
      partner: 'Green Impact Fund',
      type: 'Investment Partner',
      result: 'Enabled expansion to 3 new production facilities',
      quote: 'MY AVA Farms represents the perfect blend of profitability and environmental impact.',
      image: 'https://readdy.ai/api/search-image?query=African%20investment%20partners%20visiting%20expanded%20agricultural%20facility%2C%20successful%20business%20growth%20in%20Ghana%2C%20professional%20investors%20touring%20BSF%20farming%20operation%2C%20sustainable%20agriculture%20investment%20success&width=300&height=200&seq=success-story-3&orientation=landscape'
    }
  ];

  return (
    <>
      <SEO
        title="BSF Farming Partnerships | MY AVA Farms Ghana"
        description="Partner with MY AVA Farms for BSF farming distribution, research collaboration, investment opportunities, technology partnerships, training affiliates, and export partnerships in Accra Ghana. Join Ghana's sustainable agriculture revolution."
        keywords="BSF farming partnerships Ghana, agricultural partnerships Accra, insect farming investment Ghana, BSF distribution partners, research collaboration Ghana, sustainable agriculture partnerships"
        ogImage="https://readdy.ai/api/search-image?query=African%20business%20professionals%20shaking%20hands%20partnership%20agreement%20sustainable%20agriculture%20Ghana%2C%20modern%20professional%20setting%2C%20collaboration%20teamwork%2C%20bright%20natural%20tones&width=1200&height=630&seq=og-partnerships-myava&orientation=landscape"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(https://readdy.ai/api/search-image?query=African%20business%20partners%20collaborating%20on%20sustainable%20agriculture%20project%2C%20professional%20handshake%20in%20modern%20farming%20facility%2C%20partnership%20and%20collaboration%20in%20Ghana%20agricultural%20industry%2C%20diverse%20team%20working%20together%20on%20BSF%20farming%20innovation%2C%20bright%20professional%20atmosphere&width=1920&height=1080&seq=partnerships-hero&orientation=landscape)'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
            <div className="text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Partner With Us
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-4xl mx-auto leading-relaxed">
                Join MY AVA Farms in building a sustainable future through innovative partnerships. 
                Together, we can revolutionize agriculture and create lasting impact.
              </p>
              <button 
                onClick={() => {
                  const formSection = document.getElementById('partnership-form');
                  if (formSection) {
                    formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Explore Partnership Opportunities
              </button>
            </div>
          </div>
        </section>

        {/* Why Partner With Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Partner With MY AVA Farms?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We offer unique advantages that make us the ideal partner for sustainable agriculture ventures
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-trophy-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Proven Track Record</h3>
                <p className="text-gray-600">Over 5 years of successful BSF farming operations in Ghana</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lightbulb-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Innovation Leader</h3>
                <p className="text-gray-600">Cutting-edge technology and sustainable farming practices</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-team-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Expert Team</h3>
                <p className="text-gray-600">Experienced professionals dedicated to partner success</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-line-chart-line text-2xl text-yellow-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Growth Potential</h3>
                <p className="text-gray-600">Rapidly expanding market with strong demand</p>
              </div>
            </div>
          </div>
        </section>

        {/* Partnership Types */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Partnership Opportunities
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover the partnership model that aligns with your goals and expertise
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {partnershipTypes.map((type, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={type.image}
                      alt={type.title}
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                        <i className={`${type.icon} text-green-600 text-xl`}></i>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">{type.title}</h3>
                    </div>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed">{type.description}</p>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Benefits:</h4>
                      <ul className="space-y-2">
                        {type.benefits.map((benefit, benefitIndex) => (
                          <li key={benefitIndex} className="flex items-start text-sm text-gray-600">
                            <i className="ri-check-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <button 
                      onClick={() => {
                        const formSection = document.getElementById('partnership-form');
                        if (formSection) {
                          formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Talk to Us
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Partnership Success Stories
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See how our partners are achieving remarkable results through collaboration
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {successStories.map((story, index) => (
                <div key={index} className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={story.image}
                      alt={story.partner}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-6">
                    <div className="mb-3">
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">{story.partner}</h3>
                      <span className="text-sm text-green-600 font-medium">{story.type}</span>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4 mb-4">
                      <p className="text-gray-900 font-semibold text-sm">{story.result}</p>
                    </div>
                    
                    <blockquote className="text-gray-600 italic text-sm">
                      "{story.quote}"
                    </blockquote>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partnership Process */}
        <section className="py-20 bg-green-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our simple 4-step process to establish a successful partnership
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Submit Application</h3>
                <p className="text-gray-600">Fill out our partnership form with your details and interests</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Initial Discussion</h3>
                <p className="text-gray-600">Meet with our team to explore opportunities and alignment</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Agreement & Setup</h3>
                <p className="text-gray-600">Finalize partnership terms and complete onboarding</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  4
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Launch & Grow</h3>
                <p className="text-gray-600">Begin collaboration with ongoing support and guidance</p>
              </div>
            </div>
          </div>
        </section>

        {/* Partnership Application Form */}
        <section id="partnership-form" className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Apply for Partnership
              </h2>
              <p className="text-xl text-gray-600">
                Take the first step towards a successful collaboration with MY AVA Farms
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <form 
                id="partnership-application" 
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="+233 XX XXX XXXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      Company/Organization *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="Your company name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="partnershipType" className="block text-sm font-semibold text-gray-700 mb-2">
                    Partnership Type *
                  </label>
                  <select
                    id="partnershipType"
                    name="partnershipType"
                    required
                    value={formData.partnershipType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="">Select partnership type</option>
                    <option value="Distribution Partner">Distribution Partner</option>
                    <option value="Research Collaboration">Research Collaboration</option>
                    <option value="Investment Opportunity">Investment Opportunity</option>
                    <option value="Technology Partner">Technology Partner</option>
                    <option value="Training Affiliate">Training Affiliate</option>
                    <option value="Export Partner">Export Partner</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Tell Us About Your Interest *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                    placeholder="Please describe your organization, partnership goals, and how you envision collaborating with MY AVA Farms..."
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    <i className="ri-check-line mr-2"></i>
                    Thank you! Your partnership application has been submitted. We'll contact you within 48 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please try again or contact us directly at myavagh@gmail.com
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-4 px-6 rounded-lg text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting Application...
                    </>
                  ) : (
                    'Submit Partnership Application'
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Have Questions About Partnerships?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Our partnership team is ready to discuss opportunities and answer your questions
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/contact')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact Partnership Team
              </button>
              <a 
                href="tel:+233233371378"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap inline-block"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Us Now
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
